import React from 'react'
import { cn } from "@/lib/utils"

// Base skeleton with shimmer animation
function Skeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-md bg-white/5",
        "before:absolute before:inset-0",
        "before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transparent",
        "before:animate-shimmer before:-translate-x-full",
        className
      )}
      {...props}
    />
  )
}

// Skeleton text line
interface SkeletonTextProps extends React.HTMLAttributes<HTMLDivElement> {
  lines?: number
  lastLineWidth?: string
}

function SkeletonText({
  lines = 3,
  lastLineWidth = "60%",
  className,
  ...props
}: SkeletonTextProps) {
  return (
    <div className={cn("space-y-2", className)} {...props}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className="h-4"
          style={{
            width: i === lines - 1 ? lastLineWidth : "100%",
            animationDelay: `${i * 100}ms`
          }}
        />
      ))}
    </div>
  )
}

// Skeleton avatar
interface SkeletonAvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg' | 'xl'
}

function SkeletonAvatar({
  size = 'md',
  className,
  ...props
}: SkeletonAvatarProps) {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  }

  return (
    <Skeleton
      className={cn("rounded-full", sizeClasses[size], className)}
      {...props}
    />
  )
}

// Skeleton card
interface SkeletonCardProps extends React.HTMLAttributes<HTMLDivElement> {
  hasImage?: boolean
  hasAvatar?: boolean
  lines?: number
}

function SkeletonCard({
  hasImage = false,
  hasAvatar = false,
  lines = 2,
  className,
  ...props
}: SkeletonCardProps) {
  return (
    <div
      className={cn(
        "rounded-xl border border-white/5 bg-white/5 p-4 space-y-4",
        className
      )}
      {...props}
    >
      {/* Image placeholder */}
      {hasImage && (
        <Skeleton className="w-full h-40 rounded-lg" />
      )}

      {/* Header with avatar */}
      {hasAvatar && (
        <div className="flex items-center gap-3">
          <SkeletonAvatar size="md" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/4" />
          </div>
        </div>
      )}

      {/* Title */}
      <Skeleton className="h-5 w-3/4" style={{ animationDelay: '50ms' }} />

      {/* Text lines */}
      <SkeletonText lines={lines} />
    </div>
  )
}

// Skeleton list item
function SkeletonListItem({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex items-center gap-4 p-4 rounded-xl border border-white/5 bg-white/5",
        className
      )}
      {...props}
    >
      <Skeleton className="w-10 h-10 rounded-lg flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-3 w-3/4" />
      </div>
      <Skeleton className="w-16 h-8 rounded-lg" />
    </div>
  )
}

// Skeleton table
interface SkeletonTableProps extends React.HTMLAttributes<HTMLDivElement> {
  rows?: number
  columns?: number
}

function SkeletonTable({
  rows = 5,
  columns = 4,
  className,
  ...props
}: SkeletonTableProps) {
  return (
    <div className={cn("space-y-2", className)} {...props}>
      {/* Header */}
      <div className="flex gap-4 p-4 bg-white/5 rounded-t-xl">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton
            key={`header-${i}`}
            className="h-4 flex-1"
            style={{ animationDelay: `${i * 50}ms` }}
          />
        ))}
      </div>

      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div
          key={`row-${rowIndex}`}
          className="flex gap-4 p-4 border-b border-white/5"
        >
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton
              key={`cell-${rowIndex}-${colIndex}`}
              className="h-4 flex-1"
              style={{ animationDelay: `${(rowIndex * columns + colIndex) * 30}ms` }}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

// Skeleton stats grid (for dashboard)
interface SkeletonStatsProps extends React.HTMLAttributes<HTMLDivElement> {
  count?: number
}

function SkeletonStats({
  count = 4,
  className,
  ...props
}: SkeletonStatsProps) {
  return (
    <div
      className={cn("grid grid-cols-2 md:grid-cols-4 gap-4", className)}
      {...props}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="p-4 rounded-xl border border-white/5 bg-white/5 space-y-3"
        >
          <Skeleton
            className="h-3 w-1/2"
            style={{ animationDelay: `${i * 100}ms` }}
          />
          <Skeleton
            className="h-8 w-2/3"
            style={{ animationDelay: `${i * 100 + 50}ms` }}
          />
          <Skeleton
            className="h-2 w-full"
            style={{ animationDelay: `${i * 100 + 100}ms` }}
          />
        </div>
      ))}
    </div>
  )
}

// Pre-defined stable heights for chart skeleton
const CHART_BAR_HEIGHTS = ['65%', '85%', '45%', '72%', '58%', '90%', '52%']

// Skeleton graph/chart placeholder
function SkeletonChart({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "relative rounded-xl border border-white/5 bg-white/5 p-4 overflow-hidden",
        className
      )}
      {...props}
    >
      {/* Chart bars */}
      <div className="flex items-end justify-around h-48 gap-2">
        {CHART_BAR_HEIGHTS.map((height, i) => (
          <Skeleton
            key={i}
            className="flex-1 rounded-t"
            style={{
              height,
              animationDelay: `${i * 100}ms`
            }}
          />
        ))}
      </div>

      {/* X-axis labels */}
      <div className="flex justify-around mt-4">
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton
            key={i}
            className="w-8 h-3"
            style={{ animationDelay: `${i * 50 + 700}ms` }}
          />
        ))}
      </div>
    </div>
  )
}

// Pulse skeleton (alternative animation)
function SkeletonPulse({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "rounded-md bg-white/5 animate-pulse",
        className
      )}
      {...props}
    />
  )
}

export {
  Skeleton,
  SkeletonText,
  SkeletonAvatar,
  SkeletonCard,
  SkeletonListItem,
  SkeletonTable,
  SkeletonStats,
  SkeletonChart,
  SkeletonPulse
}
